import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ShieldCheck, DeviceMobile, Copy, DownloadSimple, ArrowLeft, QrCode } from "@phosphor-icons/react";

const detail = (e) =>
  (e?.response?.data && formatApiErrorDetail(e.response.data.detail)) || e?.message || "Something went wrong.";

export default function MfaGate({ challenge, onSuccess, onCancel }) {
  const setupMode = !!challenge.mfa_setup_required;
  const [step, setStep] = useState(setupMode ? "choose" : "verify");
  const [totp, setTotp] = useState(null);
  const [phone, setPhone] = useState("");
  const [phoneHint, setPhoneHint] = useState(challenge.phone_hint || "");
  const [smsStage, setSmsStage] = useState("phone");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const mfaToken = challenge.mfa_token;

  const startTotp = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/start", { mfa_token: mfaToken, method: "totp" });
      setTotp(data); setCode(""); setStep("totp");
    } catch (e) { setErr(detail(e)); } finally { setLoading(false); }
  };

  const submitPhone = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/start", { mfa_token: mfaToken, method: "sms", phone });
      setPhoneHint(data.phone_hint); setCode(""); setSmsStage("code");
    } catch (e) { setErr(detail(e)); } finally { setLoading(false); }
  };

  const verifySetup = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/verify", {
        mfa_token: mfaToken, code, remember_device: remember,
      });
      setRecoveryCodes(data.recovery_codes); setPendingUser(data.user); setStep("codes");
    } catch (e) { setErr(detail(e)); } finally { setLoading(false); }
  };

  const verifyLogin = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", {
        mfa_token: mfaToken, code, remember_device: remember, use_recovery: useRecovery,
      });
      if (useRecovery && typeof data.recovery_codes_left === "number") {
        toast.warning(`Recovery code used — ${data.recovery_codes_left} left.`);
      }
      onSuccess(data.user);
    } catch (e) { setErr(detail(e)); setLoading(false); }
  };

  const resend = async () => {
    setErr("");
    try {
      await api.post("/auth/mfa/send-code", { mfa_token: mfaToken });
      toast.success("Code sent");
    } catch (e) { setErr(detail(e)); }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success("Recovery codes copied");
    } catch { toast.error("Could not copy — select and copy manually."); }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [`Safety Onsite Solutions — MFA recovery codes\nEach code works once. Keep them somewhere safe.\n\n${recoveryCodes.join("\n")}\n`],
      { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "SOS-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const codeInput = (onEnter) => (
    <input
      value={code} onChange={(e) => setCode(e.target.value)} autoFocus
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(); } }}
      inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code"
      placeholder={useRecovery ? "XXXX-XXXX" : "6-digit code"}
      data-testid="mfa-code-input"
      className="mt-1 w-full h-12 px-3 border border-slate-300 rounded-md text-center text-xl tracking-[0.3em] font-bold focus:outline-none focus:ring-2 focus:ring-green-500"
    />
  );

  const rememberBox = (
    <label className="flex items-center gap-2 mt-4 text-sm text-slate-600 cursor-pointer">
      <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
        data-testid="mfa-remember" className="h-4 w-4 accent-green-600" />
      Remember this device for 30 days
    </label>
  );

  const backLink = (
    <button type="button" onClick={onCancel} data-testid="mfa-back-to-login"
      className="mt-6 text-sm text-slate-500 hover:text-slate-800 font-semibold inline-flex items-center gap-1">
      <ArrowLeft size={14} /> Back to sign in
    </button>
  );

  const errBox = err && <div data-testid="mfa-error" className="mt-3 text-sm text-red-600">{err}</div>;

  // ---------- Setup: choose method ----------
  if (step === "choose") {
    return (
      <div className="w-full max-w-md" data-testid="mfa-setup-choose">
        <div className="tinylabel">Security upgrade</div>
        <h1 className="text-2xl sm:text-3xl font-black mt-2">Set up two-factor authentication</h1>
        <p className="text-slate-500 mt-2 text-sm">
          Admin accounts now require a second verification step at sign-in.
          Pick how you'd like to receive your codes — this takes about a minute.
        </p>
        <button type="button" onClick={startTotp} disabled={loading} data-testid="mfa-method-totp"
          className="mt-6 w-full text-left border-2 border-green-600 rounded-md p-4 hover:bg-green-50 transition-colors">
          <div className="flex items-center gap-3">
            <QrCode size={28} className="text-green-700" />
            <div>
              <div className="font-bold">Authenticator app <span className="text-xs text-green-700 font-black uppercase ml-1">Recommended</span></div>
              <div className="text-xs text-slate-500 mt-0.5">Google Authenticator, Microsoft Authenticator or Authy. Works offline.</div>
            </div>
          </div>
        </button>
        <button type="button" onClick={() => { setErr(""); setCode(""); setSmsStage("phone"); setStep("sms"); }}
          disabled={loading} data-testid="mfa-method-sms"
          className="mt-3 w-full text-left border border-slate-300 rounded-md p-4 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-3">
            <DeviceMobile size={28} className="text-slate-600" />
            <div>
              <div className="font-bold">Text message (SMS)</div>
              <div className="text-xs text-slate-500 mt-0.5">A 6-digit code texted to your cell at each sign-in.</div>
            </div>
          </div>
        </button>
        {errBox}
        {backLink}
      </div>
    );
  }

  // ---------- Setup: TOTP scan + confirm ----------
  if (step === "totp") {
    return (
      <div className="w-full max-w-md" data-testid="mfa-setup-totp">
        <div className="tinylabel">Step 1 of 2</div>
        <h1 className="text-2xl font-black mt-2">Scan this QR code</h1>
        <p className="text-slate-500 mt-2 text-sm">
          Open your authenticator app (Google Authenticator, Microsoft Authenticator, Authy…),
          tap <b>Add account</b>, and scan:
        </p>
        {totp && (
          <>
            <div className="mt-4 bg-white border border-slate-200 rounded-md p-4 inline-block" data-testid="mfa-qr">
              <QRCodeSVG value={totp.otpauth_uri} size={180} />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Can't scan? Enter this key manually:
              <div className="mt-1 font-mono text-sm bg-slate-100 rounded px-2 py-1 break-all select-all" data-testid="mfa-secret">{totp.secret}</div>
            </div>
          </>
        )}
        <label className="block text-sm font-semibold text-slate-700 mt-5">Enter the 6-digit code from the app</label>
        {codeInput(verifySetup)}
        {rememberBox}
        {errBox}
        <button type="button" onClick={verifySetup} disabled={loading || code.trim().length < 6}
          data-testid="mfa-verify-btn"
          className="mt-5 w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md disabled:opacity-60">
          {loading ? "Verifying…" : "Verify & enable"}
        </button>
        <button type="button" onClick={() => { setErr(""); setStep("choose"); }}
          className="mt-3 text-sm text-slate-500 hover:text-slate-800 font-semibold">← Choose a different method</button>
      </div>
    );
  }

  // ---------- Setup: SMS phone + confirm ----------
  if (step === "sms") {
    return (
      <div className="w-full max-w-md" data-testid="mfa-setup-sms">
        <div className="tinylabel">Step 1 of 2</div>
        <h1 className="text-2xl font-black mt-2">Verify your cell number</h1>
        {smsStage === "phone" ? (
          <>
            <p className="text-slate-500 mt-2 text-sm">We'll text a 6-digit code to this number at each sign-in.</p>
            <label className="block text-sm font-semibold text-slate-700 mt-5">Cell number</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitPhone(); } }}
              placeholder="(740) 555-1234" data-testid="mfa-phone-input"
              className="mt-1 w-full h-12 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errBox}
            <button type="button" onClick={submitPhone} disabled={loading || !phone.trim()}
              data-testid="mfa-send-code-btn"
              className="mt-5 w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md disabled:opacity-60">
              {loading ? "Sending…" : "Text me a code"}
            </button>
          </>
        ) : (
          <>
            <p className="text-slate-500 mt-2 text-sm">We texted a code to <b>{phoneHint}</b>. Enter it below.</p>
            <label className="block text-sm font-semibold text-slate-700 mt-5">6-digit code</label>
            {codeInput(verifySetup)}
            {rememberBox}
            {errBox}
            <button type="button" onClick={verifySetup} disabled={loading || code.trim().length < 6}
              data-testid="mfa-verify-btn"
              className="mt-5 w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md disabled:opacity-60">
              {loading ? "Verifying…" : "Verify & enable"}
            </button>
            <button type="button" onClick={resend} data-testid="mfa-resend-btn"
              className="mt-3 text-sm text-green-700 hover:text-green-800 font-semibold">Resend code</button>
          </>
        )}
        <div>
          <button type="button" onClick={() => { setErr(""); setStep("choose"); }}
            className="mt-3 text-sm text-slate-500 hover:text-slate-800 font-semibold">← Choose a different method</button>
        </div>
      </div>
    );
  }

  // ---------- Setup: recovery codes ----------
  if (step === "codes") {
    return (
      <div className="w-full max-w-md" data-testid="mfa-recovery-codes">
        <div className="flex items-center gap-2 text-green-700">
          <ShieldCheck size={28} weight="fill" />
          <span className="font-black text-lg">Two-factor authentication is on</span>
        </div>
        <h1 className="text-2xl font-black mt-4">Save your recovery codes</h1>
        <p className="text-slate-500 mt-2 text-sm">
          If you ever lose your phone, one of these codes gets you back in.
          <b> Each works once.</b> Store them somewhere safe — you won't see them again.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-md p-4 font-mono text-sm">
          {(recoveryCodes || []).map((c) => <div key={c} className="select-all">{c}</div>)}
        </div>
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={copyCodes} data-testid="mfa-copy-codes"
            className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold inline-flex items-center gap-2 hover:bg-slate-50">
            <Copy size={15} /> Copy
          </button>
          <button type="button" onClick={downloadCodes} data-testid="mfa-download-codes"
            className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold inline-flex items-center gap-2 hover:bg-slate-50">
            <DownloadSimple size={15} /> Download .txt
          </button>
        </div>
        <button type="button" onClick={() => onSuccess(pendingUser)} data-testid="mfa-continue-btn"
          className="mt-6 w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md">
          I've saved them — continue
        </button>
      </div>
    );
  }

  // ---------- Login: verify ----------
  return (
    <div className="w-full max-w-md" data-testid="mfa-verify-screen">
      <div className="tinylabel">Two-factor verification</div>
      <h1 className="text-2xl sm:text-3xl font-black mt-2">Enter your code</h1>
      <p className="text-slate-500 mt-2 text-sm">
        {useRecovery
          ? "Enter one of your saved recovery codes. Each works once."
          : challenge.method === "sms"
            ? `We texted a 6-digit code to ${phoneHint || "your phone"}.`
            : "Open your authenticator app and enter the 6-digit code for Safety Onsite Solutions."}
      </p>
      <label className="block text-sm font-semibold text-slate-700 mt-5">
        {useRecovery ? "Recovery code" : "6-digit code"}
      </label>
      {codeInput(verifyLogin)}
      {rememberBox}
      {errBox}
      <button type="button" onClick={verifyLogin} disabled={loading || code.trim().length < 6}
        data-testid="mfa-verify-btn"
        className="mt-5 w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md disabled:opacity-60">
        {loading ? "Verifying…" : "Verify"}
      </button>
      <div className="mt-4 flex items-center justify-between text-sm">
        <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setErr(""); }}
          data-testid="mfa-use-recovery"
          className="text-slate-600 hover:text-slate-900 font-semibold">
          {useRecovery ? "Use my normal code" : "Use a recovery code"}
        </button>
        {challenge.method === "sms" && !useRecovery && (
          <button type="button" onClick={resend} data-testid="mfa-resend-btn"
            className="text-green-700 hover:text-green-800 font-semibold">Resend code</button>
        )}
      </div>
      {backLink}
    </div>
  );
}
